<template>
  <Modal class="max-w-[700px]!">
    <div
      class="pointer-events-none absolute -top-5 -left-6 h-[180px] w-[calc(100%+48px)]"
    >
      <img :src="MapGray" class="h-full w-full object-cover opacity-60" />
      <div
        class="absolute bottom-0 h-[60px] w-full bg-gradient-to-b from-transparent to-white"
      />
    </div>

    <div class="relative">
      <div class="flex flex-col items-center pt-1.5 text-center">
        <h2 class="text-[26px] font-medium leading-[36px] text-gray-800">
          Register under national laws
        </h2>
        <p
          class="mt-1.5 max-w-[560px] text-[14px] leading-[22px] text-gray-800"
        >
          Choose additional countries to have {{ workNoun }} recognized and
          registered under their copyright laws and applicable legal remedies.
        </p>
        <a
          href="https://help.dacr.com"
          target="_blank"
          class="mt-3.5 flex items-center gap-1 text-link text-[14px]"
        >
          Learn more and registration costs
          <ExternalLinkIcon class="size-3.5" />
        </a>
      </div>

      <p class="mt-9 mb-3.5 text-[14px] leading-[22px] text-gray-600">
        Selected jurisdictions to be registered
      </p>

      <div class="grid grid-cols-3 gap-2">
        <div
          v-for="origin in lockedOrigins"
          :key="origin.id"
          class="flex h-[38px] items-center gap-2 rounded-full border-2 border-green-500 bg-green-200/20 pr-2.5 pl-1.5"
        >
          <span
            v-if="origin.id === 'Dacr'"
            class="flex size-[26px] shrink-0 items-center justify-center rounded-full border border-gray-600/15 bg-white"
          >
            <DacrMarkIcon class="size-[18px] text-gray-800" />
          </span>
          <Avatar v-else :src="origin.flag?.url" :size="26" class="shrink-0" />
          <span
            class="min-w-0 flex-1 truncate text-[13px] font-medium text-gray-800"
          >
            {{ origin.name }}
          </span>
          <Checkbox :model-value="true" disabled class="pointer-events-none" />
        </div>

        <button
          v-for="origin in selectedOrigins"
          :key="origin.id"
          type="button"
          class="flex h-[38px] cursor-pointer items-center gap-2 rounded-full border-2 border-green-500 bg-green-200/20 pr-2.5 pl-1.5"
          @click="toggleOrigin(origin.id)"
        >
          <Avatar :src="origin.flag?.url" :size="26" class="shrink-0" />
          <span
            class="min-w-0 flex-1 truncate text-left text-[13px] font-medium text-gray-800"
          >
            {{ origin.name }}
          </span>
          <Checkbox :model-value="true" class="pointer-events-none" />
        </button>
      </div>

      <template v-if="availableOrigins.length">
        <p class="mt-6 mb-3.5 text-[14px] leading-[22px] text-gray-600">
          Jurisdictions available to be registered
        </p>

        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="origin in availableOrigins"
            :key="origin.id"
            type="button"
            class="flex h-[38px] cursor-pointer items-center gap-2 rounded-full border border-black/8 bg-white pr-2.5 pl-1.5 transition-colors hover:border-black/15"
            @click="toggleOrigin(origin.id)"
          >
            <Avatar :src="origin.flag?.url" :size="26" class="shrink-0" />
            <span
              class="min-w-0 flex-1 truncate text-left text-[13px] font-medium text-gray-800"
            >
              {{ origin.name }}
            </span>
            <Checkbox :model-value="false" class="pointer-events-none" />
          </button>
        </div>
      </template>

      <div class="mt-7 flex justify-end gap-2.5">
        <Button variant="stroke" size="md" @click="close()">Cancel</Button>
        <Button
          variant="primary"
          size="md"
          :disabled="!canSave"
          @click="proceed"
        >
          Register
        </Button>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { Modal, Button, Avatar, Checkbox, useModal } from '@dacr/library'
import { DacrMarkIcon, ExternalLinkIcon } from '@dacr/icons'
import MapGray from '@/assets/images/MapGray.png'
import type { File } from '@/shared/graphql/router/graphql'
import { useRegisterCountries } from './useRegisterCountries'

const { close } = useModal()

const props = defineProps<{
  files: File[]
}>()

const {
  lockedOrigins,
  selectedOrigins,
  availableOrigins,
  toggleOrigin,
  canSave,
  workNoun,
  proceed,
} = useRegisterCountries(props.files)
</script>
